// 本地檔案管理服務
export interface LocalFileRecord {
  id: string;
  relativePath: string;
  fileName: string;
  fileType: 'video' | 'image' | 'document' | 'other';
  fileSize: number;
  lastModified: number;
  lastOpened: number;
  thumbnail?: string;
  thumbnailUrl?: string; // 🔧 修復：添加 thumbnailUrl 屬性（與 thumbnail 相同）
  notes: Record<number, LocalFileNote>;
}

export interface LocalFileNote {
  timestamp: number;
  note: string;
  timecode: string;
  keyNumber: number;
}

export interface WorkspaceConfig {
  workspacePath: string;
  lastSyncTime: number;
  fileCount: number;
}

class LocalFileService {
  private readonly WORKSPACE_KEY = 'checkplay_workspace_path';
  private readonly LOCAL_FILES_KEY = 'checkplay_local_files';
  private readonly LOCAL_NOTES_KEY = 'checkplay_local_notes';

  // 🔧 新增：獲取用戶相關的 localStorage key
  private getUserKey(baseKey: string, userId?: string): string {
    return userId ? `${baseKey}_${userId}` : baseKey;
  }

  // 獲取工作目錄設定
  getWorkspacePath(userId?: string): string | null {
    const key = this.getUserKey(this.WORKSPACE_KEY, userId);
    return localStorage.getItem(key);
  }

  // 設定工作目錄
  setWorkspacePath(path: string, userId?: string): void {
    const key = this.getUserKey(this.WORKSPACE_KEY, userId);
    localStorage.setItem(key, path);
  }

  // 清除工作目錄
  clearWorkspace(userId?: string): void {
    const workspaceKey = this.getUserKey(this.WORKSPACE_KEY, userId);
    const filesKey = this.getUserKey(this.LOCAL_FILES_KEY, userId);
    const notesKey = this.getUserKey(this.LOCAL_NOTES_KEY, userId);
    
    localStorage.removeItem(workspaceKey);
    localStorage.removeItem(filesKey);
    localStorage.removeItem(notesKey);
  }

  // 獲取本地檔案列表
  getLocalFiles(userId?: string): LocalFileRecord[] {
    const key = this.getUserKey(this.LOCAL_FILES_KEY, userId);
    const filesJson = localStorage.getItem(key);
    return filesJson ? JSON.parse(filesJson) : [];
  }

  // 儲存本地檔案列表
  saveLocalFiles(files: LocalFileRecord[], userId?: string): void {
    const key = this.getUserKey(this.LOCAL_FILES_KEY, userId);
    localStorage.setItem(key, JSON.stringify(files));
  }

  // 獲取檔案的 Note 資料
  getFileNotes(fileId: string, userId?: string): Record<number, LocalFileNote> {
    const key = this.getUserKey(`${this.LOCAL_NOTES_KEY}_${fileId}`, userId);
    const notesJson = localStorage.getItem(key);
    return notesJson ? JSON.parse(notesJson) : {};
  }

  // 儲存檔案的 Note 資料
  saveFileNotes(fileId: string, notes: Record<number, LocalFileNote>, userId?: string): void {
    const key = this.getUserKey(`${this.LOCAL_NOTES_KEY}_${fileId}`, userId);
    localStorage.setItem(key, JSON.stringify(notes));
  }

  // 為本地檔案生成唯一ID（修復中文字符問題）
  generateFileId(relativePath: string, lastModified: number): string {
    // 🔧 修復：使用 encodeURIComponent 處理中文字符
    const encodedPath = encodeURIComponent(relativePath);
    const hash = btoa(`${encodedPath}_${lastModified}`).replace(/[^a-zA-Z0-9]/g, '');
    return `local_${hash}`;
  }

  // 🔧 修復：真實掃描工作目錄（使用 File System Access API）
  async scanWorkspace(workspacePath: string): Promise<LocalFileRecord[]> {
    try {
      console.log('🔍 開始掃描工作目錄:', workspacePath);
      
      // 檢查是否有已保存的真實目錄檔案
      const existingFiles = this.getLocalFiles();
      const workspaceFiles = existingFiles.filter(file => 
        !file.relativePath.startsWith('uploads/') &&
        file.relativePath.includes(workspacePath)
      );
      
      if (workspaceFiles.length > 0) {
        console.log('🔍 找到已保存的工作目錄檔案:', workspaceFiles.length, '個');
        return workspaceFiles;
      }
      
      // 如果有文件系統句柄，嘗試真實掃描
      const dirHandle = this.getDirectoryHandle();
      if (dirHandle) {
        console.log('🔍 使用真實目錄句柄掃描檔案...');
        return await this.scanRealDirectory(dirHandle, workspacePath);
      }
      
      // 如果沒有真實目錄，返回模擬數據（開發階段）
      console.log('🔍 沒有找到真實目錄，返回模擬數據');
      return this.getMockFiles();
    } catch (error) {
      console.error('掃描工作目錄失敗:', error);
      return [];
    }
  }

  // 🔧 新增：保存和獲取目錄句柄
  private dirHandle: FileSystemDirectoryHandle | null = null;

  setDirectoryHandle(handle: FileSystemDirectoryHandle): void {
    this.dirHandle = handle;
  }

  getDirectoryHandle(): FileSystemDirectoryHandle | null {
    return this.dirHandle;
  }

  // 🔧 新增：真實掃描目錄
  private async scanRealDirectory(
    dirHandle: any, // FileSystemDirectoryHandle
    workspacePath: string
  ): Promise<LocalFileRecord[]> {
    const files: LocalFileRecord[] = [];
    
    try {
      // 使用 entries() 方法遍歷目錄
      for await (const [name, handle] of dirHandle.entries()) {
        if (handle.kind === 'file') {
          const file = await handle.getFile();
          const fileRecord: LocalFileRecord = {
            id: this.generateFileId(workspacePath + '/' + name, file.lastModified),
            relativePath: workspacePath + '/' + name,
            fileName: name,
            fileType: this.getFileTypeFromName(name),
            fileSize: file.size,
            lastModified: file.lastModified,
            lastOpened: Date.now(),
            notes: {}
          };
          files.push(fileRecord);
        }
      }
      
      // 保存掃描結果到 localStorage
      const existingFiles = this.getLocalFiles();
      const newFiles = [...existingFiles, ...files];
      this.saveLocalFiles(newFiles);
      
      console.log('🔍 真實掃描完成，找到檔案:', files.length, '個');
      return files;
    } catch (error) {
      console.error('掃描真實目錄失敗:', error);
      return [];
    }
  }

  // 🔧 新增：根據檔案名判斷類型
  private getFileTypeFromName(filename: string): 'video' | 'image' | 'document' | 'other' {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'];
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    const docExts = ['pdf', 'doc', 'docx', 'txt', 'md'];
    
    if (videoExts.includes(ext)) return 'video';
    if (imageExts.includes(ext)) return 'image';
    if (docExts.includes(ext)) return 'document';
    return 'other';
  }

  // 🔧 修復：獲取模擬檔案（用於開發測試）
  private getMockFiles(): LocalFileRecord[] {
    const mockFiles: LocalFileRecord[] = [
      {
        id: this.generateFileId('videos/tutorial.mp4', Date.now() - 86400000),
        relativePath: 'videos/tutorial.mp4',
        fileName: 'tutorial.mp4',
        fileType: 'video',
        fileSize: 1024 * 1024 * 50, // 50MB
        lastModified: Date.now() - 86400000,
        lastOpened: Date.now() - 3600000,
        notes: {}
      },
      {
        id: this.generateFileId('images/screenshot.png', Date.now() - 3600000),
        relativePath: 'images/screenshot.png',
        fileName: 'screenshot.png',
        fileType: 'image',
        fileSize: 1024 * 512, // 512KB
        lastModified: Date.now() - 3600000,
        lastOpened: Date.now() - 1800000,
        notes: {}
      },
      {
        id: this.generateFileId('documents/report.pdf', Date.now() - 172800000),
        relativePath: 'documents/report.pdf',
        fileName: 'report.pdf',
        fileType: 'document',
        fileSize: 1024 * 1024 * 2, // 2MB
        lastModified: Date.now() - 172800000,
        lastOpened: Date.now() - 7200000,
        notes: {}
      }
    ];

    // 載入現有的 Note 資料
    mockFiles.forEach(file => {
      const existingNotes = this.getFileNotes(file.id);
      if (Object.keys(existingNotes).length > 0) {
        file.notes = existingNotes;
      }
    });

    return mockFiles;
  }

  // 新增或更新 Note
  addOrUpdateNote(fileId: string, keyNumber: number, note: string, timestamp: number, timecode: string): void {
    const notes = this.getFileNotes(fileId);
    notes[keyNumber] = {
      timestamp,
      note,
      timecode,
      keyNumber
    };
    this.saveFileNotes(fileId, notes);
  }

  // 刪除 Note
  deleteNote(fileId: string, keyNumber: number): void {
    const notes = this.getFileNotes(fileId);
    delete notes[keyNumber];
    this.saveFileNotes(fileId, notes);
  }

  // 獲取檔案的所有 Note
  getAllNotes(fileId: string): LocalFileNote[] {
    const notes = this.getFileNotes(fileId);
    return Object.values(notes).sort((a, b) => a.keyNumber - b.keyNumber);
  }

  // 檢查檔案是否有 Note
  hasNotes(fileId: string): boolean {
    const notes = this.getFileNotes(fileId);
    return Object.keys(notes).length > 0;
  }

  // 更新檔案的最後開啟時間
  updateLastOpened(fileId: string): void {
    const files = this.getLocalFiles();
    const fileIndex = files.findIndex(f => f.id === fileId);
    if (fileIndex !== -1) {
      files[fileIndex].lastOpened = Date.now();
      this.saveLocalFiles(files);
    }
  }

  // 搜尋本地檔案
  searchLocalFiles(query: string): LocalFileRecord[] {
    const files = this.getLocalFiles();
    if (!query.trim()) return files;

    const lowerQuery = query.toLowerCase();
    return files.filter(file => 
      file.fileName.toLowerCase().includes(lowerQuery) ||
      file.relativePath.toLowerCase().includes(lowerQuery) ||
      Object.values(file.notes).some(note => 
        note.note.toLowerCase().includes(lowerQuery)
      )
    );
  }

  // 按類型篩選檔案
  filterFilesByType(fileType: string): LocalFileRecord[] {
    const files = this.getLocalFiles();
    if (fileType === 'all') return files;
    return files.filter(file => file.fileType === fileType);
  }

  // 按最近開啟時間排序
  sortFilesByLastOpened(): LocalFileRecord[] {
    const files = this.getLocalFiles();
    return [...files].sort((a, b) => b.lastOpened - a.lastOpened);
  }

  // 按檔案大小排序
  sortFilesBySize(): LocalFileRecord[] {
    const files = this.getLocalFiles();
    return [...files].sort((a, b) => b.fileSize - a.fileSize);
  }

  // 按修改時間排序
  sortFilesByModified(): LocalFileRecord[] {
    const files = this.getLocalFiles();
    return [...files].sort((a, b) => b.lastModified - a.lastModified);
  }

  // 獲取工作目錄統計資訊
  getWorkspaceStats(): { totalFiles: number; totalSize: number; filesWithNotes: number } {
    const files = this.getLocalFiles();
    const totalFiles = files.length;
    const totalSize = files.reduce((sum, file) => sum + file.fileSize, 0);
    const filesWithNotes = files.filter(file => this.hasNotes(file.id)).length;

    return { totalFiles, totalSize, filesWithNotes };
  }

  // 匯出工作目錄資料
  exportWorkspaceData(): string {
    const workspacePath = this.getWorkspacePath();
    const files = this.getLocalFiles();
    const exportData = {
      workspacePath,
      files: files.map(file => ({
        ...file,
        notes: this.getFileNotes(file.id)
      })),
      exportTime: new Date().toISOString()
    };
    return JSON.stringify(exportData, null, 2);
  }

  // 匯入工作目錄資料
  importWorkspaceData(data: string): boolean {
    try {
      const importData = JSON.parse(data);
      if (importData.workspacePath && importData.files) {
        this.setWorkspacePath(importData.workspacePath);
        this.saveLocalFiles(importData.files);
        
        // 匯入 Note 資料
        importData.files.forEach((file: LocalFileRecord) => {
          if (file.notes && Object.keys(file.notes).length > 0) {
            this.saveFileNotes(file.id, file.notes);
          }
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('匯入工作目錄資料失敗:', error);
      return false;
    }
  }

  // 🔧 新增：手動添加工作目錄檔案（用於測試）
  addWorkspaceFile(fileInfo: {
    fileName: string;
    relativePath: string;
    fileType: 'video' | 'image' | 'document' | 'other';
    fileSize: number;
  }, userId?: string) {
    try {
      const localFileRecord: LocalFileRecord = {
        id: this.generateFileId(fileInfo.relativePath, Date.now()),
        relativePath: fileInfo.relativePath,
        fileName: fileInfo.fileName,
        fileType: fileInfo.fileType,
        fileSize: fileInfo.fileSize,
        lastModified: Date.now(),
        lastOpened: Date.now(),
        notes: {}
      };

      // 儲存到 localStorage
      const existingFiles = this.getLocalFiles(userId);
      existingFiles.push(localFileRecord);
      this.saveLocalFiles(existingFiles, userId);

      console.log('✅ 已添加工作目錄檔案:', localFileRecord.fileName);
      return localFileRecord;
    } catch (error) {
      console.error('❌ 添加工作目錄檔案失敗:', error);
      throw error;
    }
  };

  // 🔧 新增：將上傳的檔案記錄到本地檔案服務
  addUploadedFile = async (fileInfo: {
    filename: string;
    originalFilename: string;
    fileSize: number;
    contentType: string;
    uploadTime: number;
    thumbnailUrl?: string;
  }, userId?: string) => {
    try {
      const localFileRecord: LocalFileRecord = {
        id: fileInfo.filename,
        relativePath: `uploads/${fileInfo.filename}`,
        fileName: fileInfo.originalFilename,
        fileType: this.getFileTypeFromContentType(fileInfo.contentType),
        fileSize: fileInfo.fileSize,
        lastModified: fileInfo.uploadTime,
        lastOpened: fileInfo.uploadTime,
        thumbnail: fileInfo.thumbnailUrl,
        notes: {}
      };

      // 儲存到 localStorage
      const existingFiles = this.getLocalFiles(userId);
      existingFiles.push(localFileRecord);
      this.saveLocalFiles(existingFiles, userId);

      console.log('✅ 已將上傳檔案記錄到本地檔案服務:', localFileRecord.fileName);
      return localFileRecord;
    } catch (error) {
      console.error('❌ 記錄上傳檔案失敗:', error);
      throw error;
    }
  };

  // 根據內容類型判斷檔案類型
  private getFileTypeFromContentType = (contentType: string): 'video' | 'image' | 'document' | 'other' => {
    if (contentType.startsWith('video/')) return 'video';
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.includes('pdf') || contentType.includes('document')) return 'document';
    return 'other';
  };

  // 刪除檔案
  deleteFile = (fileId: string, userId?: string): void => {
    try {
      const allFiles = this.getLocalFiles(userId);
      const filteredFiles = allFiles.filter(file => file.id !== fileId);
      this.saveLocalFiles(filteredFiles, userId);
      
      // 同時刪除該檔案的 Note
      const notesKey = this.getUserKey(`${this.LOCAL_NOTES_KEY}_${fileId}`, userId);
      localStorage.removeItem(notesKey);
      
      console.log('✅ 已刪除檔案記錄:', fileId);
    } catch (error) {
      console.error('❌ 刪除檔案失敗:', error);
      throw error;
    }
  };
}

export const localFileService = new LocalFileService();
export default localFileService;
